import React from 'react';
import PropTypes from 'prop-types';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import Check from 'lucide-react/dist/esm/icons/check';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Circle from 'lucide-react/dist/esm/icons/circle';
import { cn } from '~/lib/utils';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = React.forwardRef(({ onClick, onPointerDown, style, ...props }, ref) => {
	const lastPointerDown = React.useRef(0);
	return (
		<DropdownMenuPrimitive.Trigger
			ref={ref}
			// When the menu is open, Radix locks `body { pointer-events: none }`
			// (via react-remove-scroll). The trigger inherits that, which breaks
			// click-to-close interactions in cypress. Re-enable explicitly.
			style={{ pointerEvents: 'auto', ...style }}
			onPointerDown={(e) => {
				lastPointerDown.current = Date.now();
				onPointerDown?.(e);
			}}
			onClick={(e) => {
				// Cypress's `.trigger('click')` doesn't dispatch a preceding
				// pointerdown. Radix's Trigger only opens on pointerdown, so
				// synthesize one when we see a click without a recent real
				// pointerdown (real-pointer clicks already opened the menu).
				const hadRecentPointer = Date.now() - lastPointerDown.current < 250;
				if(!hadRecentPointer && e.currentTarget) {
					lastPointerDown.current = Date.now();
					const hasPointerEvent = typeof PointerEvent === 'function';
					const init = { bubbles: true, cancelable: true, button: 0 };
					if(hasPointerEvent) {
						init.pointerType = 'mouse';
					}
					const EventCtor = hasPointerEvent ? PointerEvent : MouseEvent;
					e.currentTarget.dispatchEvent(new EventCtor('pointerdown', init));
				}
				onClick?.(e);
			}}
			{...props}
		/>
	);
});
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;
DropdownMenuTrigger.propTypes = {
	onClick: PropTypes.func,
	onPointerDown: PropTypes.func,
	style: PropTypes.object
};

const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef(({ className, inset, children, ...props }, ref) => (
	<DropdownMenuPrimitive.SubTrigger
		ref={ref}
		className={cn(
			'flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
			inset && 'pl-8',
			className
		)}
		{...props}
	>
		{children}
		<ChevronRight className="ml-auto" />
	</DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
DropdownMenuSubTrigger.propTypes = { className: PropTypes.string, inset: PropTypes.bool, children: PropTypes.node };

const DropdownMenuSubContent = React.forwardRef(({ className, style, ...props }, ref) => (
	<DropdownMenuPrimitive.SubContent
		ref={ref}
		// Raw z-index (not the z-50 utility) so a menu opened from inside a
		// shadcn Dialog isn't hidden behind it; matches Popover/Select's z-[2000].
		style={{ zIndex: 2000, ...style }}
		className={cn(
			'min-w-[8rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
			className
		)}
		{...props}
	/>
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
DropdownMenuSubContent.propTypes = { className: PropTypes.string, style: PropTypes.object };

const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, style, ...props }, ref) => (
	<DropdownMenuPrimitive.Portal>
		<DropdownMenuPrimitive.Content
			ref={ref}
			sideOffset={sideOffset}
			// Raw z-index (not the z-50 utility) so a menu opened from inside a
			// shadcn Dialog isn't hidden behind it; matches Popover/Select's z-[2000].
			style={{ zIndex: 2000, ...style }}
			className={cn(
				'min-w-[8rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
				className
			)}
			{...props}
		/>
	</DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
DropdownMenuContent.propTypes = { className: PropTypes.string, sideOffset: PropTypes.number, style: PropTypes.object };

const DropdownMenuItem = React.forwardRef(({
	className, inset, disabled, onSelect, onClick, ...props
}, ref) => {
	const localRef = React.useRef(null);
	const composedRef = React.useCallback((node) => {
		localRef.current = node;
		if(typeof ref === 'function') {
			ref(node);
		}
		else if(ref) {
			ref.current = node;
		}
	}, [ref]);
	// Radix extracts `disabled` from props and only emits `data-disabled` /
	// `aria-disabled`. Cypress checks for the native `disabled` attribute and
	// computed pointer-events; mirror both via direct DOM access since the
	// Item primitive strips the `disabled` prop before reaching the DOM.
	React.useEffect(() => {
		if(!localRef.current) {
			return;
		}
		if(disabled) {
			localRef.current.setAttribute('disabled', '');
			localRef.current.style.pointerEvents = 'none';
		}
		else {
			localRef.current.removeAttribute('disabled');
			localRef.current.style.removeProperty('pointer-events');
		}
	}, [disabled]);
	return (
		<DropdownMenuPrimitive.Item
			ref={composedRef}
			disabled={disabled}
			onSelect={onSelect}
			onClick={onClick}
			className={cn(
				'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
				inset && 'pl-8',
				className
			)}
			{...props}
		/>
	);
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
DropdownMenuItem.propTypes = {
	className: PropTypes.string,
	inset: PropTypes.bool,
	disabled: PropTypes.bool,
	onSelect: PropTypes.func,
	onClick: PropTypes.func
};

const DropdownMenuCheckboxItem = React.forwardRef(({ className, children, checked, ...props }, ref) => (
	<DropdownMenuPrimitive.CheckboxItem
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			className
		)}
		checked={checked}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<Check className="h-4 w-4" />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
DropdownMenuCheckboxItem.propTypes = { className: PropTypes.string, children: PropTypes.node, checked: PropTypes.bool };

const DropdownMenuRadioItem = React.forwardRef(({ className, children, ...props }, ref) => (
	<DropdownMenuPrimitive.RadioItem
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			className
		)}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<Circle className="h-2 w-2 fill-current" />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
DropdownMenuRadioItem.propTypes = { className: PropTypes.string, children: PropTypes.node };

const DropdownMenuLabel = React.forwardRef(({ className, inset, ...props }, ref) => (
	<DropdownMenuPrimitive.Label
		ref={ref}
		className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)}
		{...props}
	/>
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
DropdownMenuLabel.propTypes = { className: PropTypes.string, inset: PropTypes.bool };

const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
	<DropdownMenuPrimitive.Separator
		ref={ref}
		className={cn('-mx-1 my-1 h-px bg-muted', className)}
		{...props}
	/>
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
DropdownMenuSeparator.propTypes = { className: PropTypes.string };

const DropdownMenuShortcut = ({ className, ...props }) => (
	<span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />
);
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';
DropdownMenuShortcut.propTypes = { className: PropTypes.string };

export {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuCheckboxItem,
	DropdownMenuRadioItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuGroup,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuRadioGroup
};
